/**
 * Timezone Utilities for Frontend
 * Converts UTC times to America/Chicago for display
 */

import { DateTime } from 'luxon';

/**
 * Formats UTC ISO string to America/Chicago timezone
 * @param {string} utcIso - UTC time in ISO format
 * @param {Object} options - Luxon formatting options
 * @returns {string} - Formatted time in America/Chicago timezone
 */
export function formatUtcToChicago(utcIso, options = {}) {
  try {
    if (!utcIso) return 'Invalid time';
    
    const utcTime = DateTime.fromISO(utcIso, { zone: 'utc' });
    if (!utcTime.isValid) {
      console.warn('Invalid UTC time:', utcIso);
      return 'Invalid time';
    }
    
    const chicagoTime = utcTime.setZone('America/Chicago');
    
    // Default formatting options
    const defaultOptions = {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    };
    
    const formatOptions = { ...defaultOptions, ...options };
    return chicagoTime.toLocaleString(formatOptions);
  } catch (error) {
    console.error('Error formatting UTC to Chicago:', error);
    return 'Invalid time';
  }
}

/**
 * Formats UTC ISO string to Chicago time only
 * @param {string} utcIso - UTC time in ISO format
 * @returns {string} - Time in America/Chicago timezone
 */
export function formatUtcTimeToChicago(utcIso) {
  return formatUtcToChicago(utcIso, {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true
  });
}

/**
 * Formats UTC ISO string to Chicago date only
 * @param {string} utcIso - UTC time in ISO format
 * @returns {string} - Date in America/Chicago timezone
 */
export function formatUtcDateToChicago(utcIso) {
  return formatUtcToChicago(utcIso, {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });
}

/**
 * Formats UTC ISO string to Chicago date and time
 * @param {string} utcIso - UTC time in ISO format
 * @returns {string} - Date and time in America/Chicago timezone
 */
export function formatUtcDateTimeToChicago(utcIso) {
  return formatUtcToChicago(utcIso, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true
  });
}

/**
 * Gets relative time from UTC ISO string in Chicago timezone
 * @param {string} utcIso - UTC time in ISO format
 * @returns {string} - Relative time (e.g., "2 hours ago", "in 30 minutes")
 */
export function getRelativeTimeInChicago(utcIso) {
  try {
    if (!utcIso) return 'Invalid time';
    
    const utcTime = DateTime.fromISO(utcIso, { zone: 'utc' });
    if (!utcTime.isValid) {
      return 'Invalid time';
    }
    
    const chicagoTime = utcTime.setZone('America/Chicago');
    const now = DateTime.now().setZone('America/Chicago');
    
    return chicagoTime.toRelative({ base: now });
  } catch (error) {
    console.error('Error getting relative time:', error);
    return 'Invalid time';
  }
}

/**
 * Checks if a UTC time is in the past relative to now in Chicago timezone
 * @param {string} utcIso - UTC time in ISO format
 * @returns {boolean} - true if the time is in the past
 */
export function isPastTimeInChicago(utcIso) {
  try {
    if (!utcIso) return false;
    
    const utcTime = DateTime.fromISO(utcIso, { zone: 'utc' });
    if (!utcTime.isValid) return false;
    
    const chicagoTime = utcTime.setZone('America/Chicago');
    const now = DateTime.now().setZone('America/Chicago');
    
    return chicagoTime < now;
  } catch (error) {
    console.error('Error checking if time is past:', error);
    return false;
  }
}

/**
 * Checks if a UTC time is in the future relative to now in Chicago timezone
 * @param {string} utcIso - UTC time in ISO format
 * @returns {boolean} - true if the time is in the future
 */
export function isFutureTimeInChicago(utcIso) {
  try {
    if (!utcIso) return false;
    
    const utcTime = DateTime.fromISO(utcIso, { zone: 'utc' });
    if (!utcTime.isValid) return false;
    
    const chicagoTime = utcTime.setZone('America/Chicago');
    const now = DateTime.now().setZone('America/Chicago');
    
    return chicagoTime > now;
  } catch (error) {
    console.error('Error checking if time is future:', error);
    return false;
  }
}

/**
 * Gets the current time in Chicago timezone
 * @returns {DateTime} - Current time in America/Chicago timezone
 */
export function getCurrentChicagoTime() {
  return DateTime.now().setZone('America/Chicago');
}

/**
 * Gets the current time in UTC
 * @returns {DateTime} - Current time in UTC
 */
export function getCurrentUtcTime() {
  return DateTime.utc();
}

/**
 * Converts Chicago time to UTC ISO string
 * @param {string} chicagoTime - Time in America/Chicago timezone
 * @returns {string} - UTC time in ISO format
 */
export function convertChicagoToUtc(chicagoTime) {
  try {
    const chicago = DateTime.fromISO(chicagoTime, { zone: 'America/Chicago' });
    if (!chicago.isValid) {
      console.warn('Invalid Chicago time:', chicagoTime);
      return null;
    }
    
    return chicago.toUTC().toISO();
  } catch (error) {
    console.error('Error converting Chicago to UTC:', error);
    return null;
  }
}
